import React, { FC, useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { createMedicationRegex } from '@/shared/constants/medicationPatterns'
import { useToast } from '@/shared/contexts/ToastContext'
import { BodyText } from '@/components/atoms/BodyText'
import { MetaText } from '@/components/atoms/MetaText'
import { DialogHeader } from '@/components/atoms/DialogHeader'
import { RADIUS } from '@/shared/styles/radii'
import { NOTION_DIALOG_PAPER_SX } from '@/shared/styles/dialogStyles'

// design/13 §4.3 適用. SOAP 表示時に 【xxx】 placeholder + 実 medication 名そのものを
// regex で検出し、 click で Notion popover dialog を開いて実 medication 名で置換する.
// 機能粒度: 1 placeholder = 1 medication を選んで即置換. row click で即決 + auto close.
// 一括置換 / multi-select は同居させず、 必要なら編集モードの BulkReplaceDialog で別途.

type Props = {
  text: string
  medInfo: string[]
  onReplace: (
    original: string,
    replacement: string,
    replaceAll?: boolean
  ) => Promise<boolean>
}

export const MedicationPlaceholderText: FC<Props> = ({
  text,
  medInfo,
  onReplace,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [currentPlaceholder, setCurrentPlaceholder] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const { showToast } = useToast()

  const medPlaceholderRegex = createMedicationRegex(medInfo)

  const renderText = () => {
    const parts: { text: string; isPlaceholder: boolean }[] = []
    let lastIndex = 0
    let match
    medPlaceholderRegex.lastIndex = 0
    while ((match = medPlaceholderRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          text: text.substring(lastIndex, match.index),
          isPlaceholder: false,
        })
      }
      parts.push({ text: match[0], isPlaceholder: true })
      lastIndex = match.index + match[0].length
    }
    if (lastIndex < text.length) {
      parts.push({ text: text.substring(lastIndex), isPlaceholder: false })
    }

    return parts.map((part, index) =>
      part.isPlaceholder ? (
        <Box
          key={index}
          component="span"
          sx={{
            cursor: 'pointer',
            borderRadius: `${RADIUS.sm}px`,
            px: 0.25,
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            textDecorationColor: 'rgba(55,53,47,0.32)',
            textUnderlineOffset: '2px',
            transition: 'background-color 120ms',
            '&:hover': { bgcolor: 'rgba(55,53,47,0.08)' },
          }}
          onClick={(e) => {
            // 親の SOAP 行が onClick=除外トグルを持つため、 プレースホルダ押下が
            // 行除外に bubble しないよう止める (= 薬剤選択ダイアログのみ開く).
            e.stopPropagation()
            setCurrentPlaceholder(part.text)
            setIsDialogOpen(true)
          }}
        >
          {part.text}
        </Box>
      ) : (
        <span key={index}>{part.text}</span>
      )
    )
  }

  const handleSelect = async (medication: string) => {
    if (isUpdating || medication === currentPlaceholder) {
      setIsDialogOpen(false)
      return
    }
    try {
      setIsUpdating(true)
      const ok = await onReplace(currentPlaceholder, medication)
      if (!ok) {
        showToast('薬剤の置換に失敗しました', 'error')
      }
    } catch {
      showToast('薬剤置換中にエラーが発生しました', 'error')
    } finally {
      setIsUpdating(false)
      setIsDialogOpen(false)
    }
  }

  const handleClose = () => {
    if (isUpdating) return
    setIsDialogOpen(false)
  }

  return (
    <>
      <Box component="span" sx={{ display: 'inline' }}>
        {renderText()}
      </Box>
      <Dialog
        open={isDialogOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        sx={{ '& .MuiDialog-paper': NOTION_DIALOG_PAPER_SX }}
      >
        <DialogHeader
          title={`「${currentPlaceholder}」 を置換`}
          onClose={handleClose}
          disabled={isUpdating}
        />
        <DialogContent sx={{ p: 0.5, maxHeight: 320 }}>
          {medInfo.length === 0 ? (
            <Box sx={{ px: 1, py: 1.5 }}>
              <MetaText sx={{ color: 'text.disabled' }}>
                処方薬情報がありません
              </MetaText>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {medInfo.map((med, index) => {
                const isCurrent = med === currentPlaceholder
                return (
                  <ListItem key={index} disablePadding>
                    <ListItemButton
                      onClick={() => void handleSelect(med)}
                      disabled={isUpdating}
                      sx={{
                        height: 32,
                        px: 1,
                        borderRadius: `${RADIUS.sm}px`,
                        gap: 1,
                        '&:hover': { bgcolor: 'rgba(55,53,47,0.06)' },
                      }}
                    >
                      <BodyText
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {med}
                      </BodyText>
                      {isCurrent && (
                        <CheckIcon
                          sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
